import React, { type FC } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FinalDecisionMakingProperty } from '../../utility/types';

export type FinalDecisionExportPdfButtonProps = {
  properties: FinalDecisionMakingProperty[];
  listTitle: string;
}

// Same columns, in the same order, as FinalDecisionMakingTable (minus the edit/delete action columns)
const PDF_COLUMNS: { header: string; field: string }[] = [
  { header: 'Auction ID#', field: 'customListSortNumber' },
  { header: 'Address', field: 'address' },
  { header: 'Estimated Value', field: 'arvSelected' },
  { header: 'Approach', field: 'approachName' },
  { header: 'Max Bid', field: 'maxBid' },
  { header: 'Repair Est.', field: 'repairEstimate' },
  { header: 'Profit on Max Bid', field: 'profitOnMaxBid' },
  { header: 'ROI %', field: 'roiPercent' },
  { header: 'SqFt', field: 'sqFt' },
  { header: 'Year Built', field: 'yearBuilt' },
  { header: 'Bed', field: 'bedrooms' },
  { header: 'Bath', field: 'bathrooms' },
  { header: 'Acreage', field: 'acreage' },
];

const toCell = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const safeFileName = (title: string): string => {
  return title.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'final-decision';
};

export const exportFinalDecisionPdf = (properties: FinalDecisionMakingProperty[], listTitle: string): void => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const generatedOn = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  doc.setFontSize(16);
  doc.text(`Final Decision: ${listTitle}`, 40, 40);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated ${generatedOn}  |  ${properties.length} ${properties.length === 1 ? 'property' : 'properties'}`, 40, 56);

  autoTable(doc, {
    startY: 70,
    head: [ PDF_COLUMNS.map((col) => col.header) ],
    body: properties.map((property) => PDF_COLUMNS.map((col) => toCell(property[col.field]))),
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [ 79, 70, 229 ], textColor: 255 },
    alternateRowStyles: { fillColor: [ 245, 245, 250 ] },
    columnStyles: { 1: { cellWidth: 150 } },
    margin: { left: 40, right: 40 },
    didDrawPage: () => {
      const pageNumber = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Page ${pageNumber}`, doc.internal.pageSize.getWidth() - 70, doc.internal.pageSize.getHeight() - 20);
    },
  });

  doc.save(`final-decision-${safeFileName(listTitle)}.pdf`);
};

const FinalDecisionExportPdfButton: FC<FinalDecisionExportPdfButtonProps> = ({ properties, listTitle }) => {
  const disabled = !properties || properties.length === 0;

  return (
    <button
      type='button'
      id='fdm-export-pdf-button'
      data-tag='fdm-export-pdf-button'
      disabled={disabled}
      title={disabled ? 'No properties to export' : 'Download this list as a PDF'}
      className='rounded bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
      onClick={() => { exportFinalDecisionPdf(properties, listTitle || 'Final Decision'); }}
    >
      Print / Export PDF
    </button>
  );
};

export default FinalDecisionExportPdfButton;
